import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { JourneyFormButtonArg } from "../../../../types/dashboard/JourneyFormDropDownItems";
import JourneyListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingUserDetailDto";
import JourneyListingDto from "../../../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faPaperPlane,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheckCircle, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import dateHelper from "../../../../helpers/dateHelper";
import journeyFormHelper from "../../../../helpers/journeyFormHelper";
import listingHelper from "../../../../helpers/listingHelper";

interface JourneyUserJourneyItemProps {
  user: JourneyListingUserDetailDto;
  journey: JourneyListingDto;
  hasPreventJourneySwitchConfigEnabled: boolean;
  onUpdateClick: (journey: JourneyListingDto) => void;
  onViewClick: (journey: JourneyListingDto) => void;
  onDualPrepItemClick: (
    user: JourneyListingUserDetailDto,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => void;
}

const JourneyUserJourneyItem: React.FC<JourneyUserJourneyItemProps> = ({
  user,
  journey,
  hasPreventJourneySwitchConfigEnabled,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
}) => {
  const { t } = useTranslation();
  const [hasButtonBeenClicked, setHasButtonBeenClicked] =
    useState<boolean>(false);

  // Determine if the journey is completed (similar to isCompletedTab logic)
  const isCompleted = journey.overallStatus === "COMPLETED";
  const isDualPrepJourney = journey.isDualPrepJourney || false;

  // Helper function to get dual prep status information (similar to JourneyFormItem)
  const getDualPrepStatusInfo = () => {
    if (!journey.isDualPrepJourney) return null;

    // Need to check if the isEmpPlanningPartiallySaved, though the isDualPrepSubjectAnswerSet
    // will not be set at this point. Need to check there is only one AnswerSet and it is partially saved.
    const isEmpPlanningPartiallySaved =
      journey.answerSets.length === 1 &&
      journey.answerSets[0].statusEnum === "PARTIALLY-SAVED-JOURNEY";

    const doesEmpPlanningExist = journey.answerSets.some(
      (x) => x.isDualPrepSubjectAnswerSet
    );
    const employeeCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepSubjectAnswerSet && x.statusEnum === "DUAL-PREP-SUBMITTED"
    );
    const doesManPlanningExist = journey.answerSets.some(
      (x) => x.isDualPrepOtherAnswerSet
    );
    const managerCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepOtherAnswerSet && x.statusEnum === "DUAL-PREP-SUBMITTED"
    );
    const doesMeetingDocExist = journey.answerSets.some(
      (x) => x.isDualPrepMeetingDocAnswerSet
    );
    const meetingDocCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepMeetingDocAnswerSet && x.statusEnum === "FULLY-APPROVED"
    );

    // Get manager name (simplified for now - could be enhanced with actual manager data)
    const managerName = "Manager"; // This could be passed as prop or retrieved from context

    return {
      isEmpPlanningPartiallySaved,
      doesEmpPlanningExist,
      employeeCompleted,
      doesManPlanningExist,
      managerCompleted,
      doesMeetingDocExist,
      meetingDocCompleted,
      managerName,
    };
  };

  const dualPrepInfo = getDualPrepStatusInfo();

  // Calculate button text based on status
  const standardButtonText =
    isCompleted || journey.isForceClosed
      ? t("Common.View")
      : t("Common.Update");

  // Logic for determining if button should be disabled
  const journeyNotStartedOrPartiallyCompleteStatus =
    journey.hasNotBeenStarted ||
    (journey.approvalStatusEnum !== null &&
      journey.approvalStatusEnum === "PARTIALLY-SAVED-JOURNEY");

  // On the manager version if the user hasn't started the journey yet disable the update
  // button as the manager can't update until that user has done the journey
  const disableStandardButton = journeyNotStartedOrPartiallyCompleteStatus;

  // Dual prep functionality
  const dualPrepDropDownButtonItems =
    journeyFormHelper.getDualPrepDropdownButtonItems(
      journey,
      isCompleted,
      true, // isYourPeoplePage
      hasPreventJourneySwitchConfigEnabled
    );

  const disablePrimaryDualPrepButton =
    dualPrepDropDownButtonItems && dualPrepDropDownButtonItems.length > 0
      ? dualPrepDropDownButtonItems[0].disabled
      : true;

  // Handle standard button click for non-dual prep
  const handleStandardButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHasButtonBeenClicked(true);
    if (isCompleted || journey.isForceClosed) {
      onViewClick(journey);
    } else {
      onUpdateClick(journey);
    }
  };

  // Handle dual prep button click
  const handleDualPrepButtonClick = (
    e: React.MouseEvent,
    arg: JourneyFormButtonArg
  ) => {
    e.stopPropagation();
    setHasButtonBeenClicked(true);
    onDualPrepItemClick(user, journey, arg);
  };

  // Render dual prep content section (similar to JourneyFormItem)
  const renderDualPrepContent = () => {
    if (!dualPrepInfo) return null;

    const {
      isEmpPlanningPartiallySaved,
      doesEmpPlanningExist,
      employeeCompleted,
      doesManPlanningExist,
      managerCompleted,
      doesMeetingDocExist,
      meetingDocCompleted,
    } = dualPrepInfo;

    return (
      <div className="bg-white rounded-b-md px-4 py-3 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr] gap-8 text-sm">
          {/* Dual Prep Journey indicator */}
          <div className="flex items-center">
            <span className="text-xs text-gray-600 bg-gray-100 rounded px-2 py-1">
              Dual Prep Journey
            </span>
          </div>
          {/* Manager prep status (Your Prep from manager's perspective) */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={managerCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                managerCompleted,
                true,
                doesManPlanningExist
              )}`}
              size="lg"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">Your Prep</div>
              <div className="text-xs">
                <button
                  className={`${listingHelper.getDualPrepStatusTextColor(
                    managerCompleted,
                    true,
                    doesManPlanningExist
                  )} hover:font-semibold underline font-medium transition-colors text-xs`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (managerCompleted) {
                      handleDualPrepButtonClick(e, "VIEW-MAN-PLANNING");
                    } else {
                      handleDualPrepButtonClick(e, "COMPLETE-MAN-PLANNING");
                    }
                  }}
                >
                  {managerCompleted
                    ? "View"
                    : doesManPlanningExist
                    ? "Continue"
                    : "Start"}
                </button>
              </div>
            </div>
          </div>

          {/* Employee prep status */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={employeeCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                employeeCompleted,
                true,
                doesEmpPlanningExist || isEmpPlanningPartiallySaved
              )}`}
              size="lg"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">
                Employee's Prep
              </div>
              <div
                className={`text-xs ${listingHelper.getDualPrepStatusTextColor(
                  employeeCompleted,
                  true,
                  doesEmpPlanningExist || isEmpPlanningPartiallySaved
                )}`}
              >
                {employeeCompleted
                  ? "Completed"
                  : doesEmpPlanningExist || isEmpPlanningPartiallySaved
                  ? "In Progress"
                  : "Not started yet"}
              </div>
            </div>
          </div>

          {/* Meeting status */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={meetingDocCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                meetingDocCompleted,
                doesMeetingDocExist
              )}`}
              size="lg"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Meeting Document
              </div>
              <div className="text-xs">
                {doesMeetingDocExist ? (
                  <button
                    className={`${listingHelper.getDualPrepStatusTextColor(
                      meetingDocCompleted
                    )} hover:font-semibold underline font-medium transition-colors text-xs`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (meetingDocCompleted) {
                        handleDualPrepButtonClick(e, "VIEW-MEETING");
                      } else {
                        handleDualPrepButtonClick(e, "UPDATE-MEETING");
                      }
                    }}
                  >
                    {meetingDocCompleted ? "View" : "Continue"}
                  </button>
                ) : (
                  <span
                    className={listingHelper.getDualPrepStatusTextColor(
                      false,
                      false
                    )}
                  >
                    Not available yet
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Format the dates if they exist
  const formattedSentDate = journey.dateSent
    ? dateHelper.convertUtcDateToLocal(journey.dateSent).toLocaleDateString()
    : "Not available";

  const formattedLastUpdatedDate = journey.dateLastUpdated
    ? dateHelper
        .convertUtcDateToLocal(journey.dateLastUpdated)
        .toLocaleDateString()
    : "Not available";

  const formattedExpectedDate = journey.expectedStartDate
    ? dateHelper
        .convertUtcDateToLocal(journey.expectedStartDate)
        .toLocaleDateString()
    : "Not available";

  const isUpcoming = journey.overallStatus === "UPCOMING";

  return (
    <div className="mb-3 rounded-md border border-gray-200 hover:shadow-sm bg-white">
      {/* Header section - consistent for all journey types */}
      <div className="flex justify-between items-center w-full py-3 px-4 bg-gray-50 rounded-md">
        <div className="flex flex-col items-start flex-grow">
          <p className="font-medium text-base text-gray-700">{journey.title}</p>

          {isUpcoming ? (
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="mr-1.5 text-gray-400"
                  size="xs"
                />
                <span>Expected {formattedExpectedDate}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className="mr-1.5 text-gray-400"
                  size="xs"
                />
                <span>Sent: {formattedSentDate}</span>
              </div>

              {journey.dateLastUpdated && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faTimer}
                      className="mr-1.5 text-gray-400"
                      size="xs"
                    />
                    <span>Updated: {formattedLastUpdatedDate}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center">
          <span
            className={`mr-4 text-xs px-2 py-0.5 rounded-full ${listingHelper.getJourneyStatusColor(
              journey
            )}`}
          >
            {listingHelper.getJourneyStatusText(journey)}
          </span>

          {/* Standard button */}
          {!journey.isDualPrepJourney && (
            <button
              className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleStandardButtonClick}
              disabled={disableStandardButton || hasButtonBeenClicked}
            >
              {hasButtonBeenClicked ? (
                <span className="flex items-center justify-center">
                  <span>{t("Common.PleaseWait")}</span>
                </span>
              ) : (
                standardButtonText
              )}
            </button>
          )}

          {/* Dual Prep button */}
          {journey.isDualPrepJourney &&
            dualPrepDropDownButtonItems.length >= 1 && (
              <button
                className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={disablePrimaryDualPrepButton || hasButtonBeenClicked}
                onClick={(e) =>
                  handleDualPrepButtonClick(
                    e,
                    dualPrepDropDownButtonItems[0].customArg
                  )
                }
              >
                {hasButtonBeenClicked ? (
                  <span className="flex items-center justify-center">
                    <span>{t("Common.PleaseWait")}</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    {standardButtonText}
                  </span>
                )}
              </button>
            )}
        </div>
      </div>

      {/* Dual prep content section - only for dual prep journeys */}
      {renderDualPrepContent()}
    </div>
  );
};

export default JourneyUserJourneyItem;
